// Runtime perspective ↔ orthographic camera swap, plus the shared camera /
// controls / viewport builders used at boot and on toggle (one source of truth).
//
// The swap recreates the three things that bind the camera at construction —
// OrbitControls, the gnomon ViewHelper, and the selection interactions. The
// render loop reads viewer.three.camera/controls live, so it needs no restart;
// setupSelectionHoverCursor, resetView, the camera intents, and keyboard input
// already read the camera live and survive the swap untouched.

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CAMERA_DEFAULTS } from '@config/defaults';
import { installCameraGnomon } from '@ui/cameraGnomon';
import { reinitSelectionInteractions } from './bootstrap/input';
import { reapplyNavMode } from './cameraModes';
import type { CameraProjection } from '@store/schema';
import type { SceneCamera, ViewerContext } from './context';

const LS_KEY = 'magviz:projection:v1';
const FOV_RAD = (CAMERA_DEFAULTS.fovDeg * Math.PI) / 180;
const HALF_FOV_TAN = Math.tan(FOV_RAD / 2);

const DEFAULT_DISTANCE = Math.hypot(
    CAMERA_DEFAULTS.position.x - CAMERA_DEFAULTS.target.x,
    CAMERA_DEFAULTS.position.y - CAMERA_DEFAULTS.target.y,
    CAMERA_DEFAULTS.position.z - CAMERA_DEFAULTS.target.z,
);

/** Persisted projection choice (defaults to perspective on any read failure). */
export function loadProjection(): CameraProjection {
    try {
        return localStorage.getItem(LS_KEY) === 'orthographic' ? 'orthographic' : 'perspective';
    } catch {
        return 'perspective';
    }
}

function saveProjection(mode: CameraProjection): void {
    try {
        localStorage.setItem(LS_KEY, mode);
    } catch {
        /* quota / private mode — the live param still reflects the choice */
    }
}

/** Projection-aware viewport refresh. Perspective updates aspect; orthographic
 *  keeps the vertical world-extent fixed and recomputes left/right from aspect. */
export function applyCameraViewport(camera: SceneCamera): void {
    const aspect = window.innerWidth / window.innerHeight;
    if (camera instanceof THREE.OrthographicCamera) {
        const halfH = (camera.top - camera.bottom) / 2;
        camera.left = -halfH * aspect;
        camera.right = halfH * aspect;
    } else {
        camera.aspect = aspect;
    }
    camera.updateProjectionMatrix();
}

/** Build a camera of the requested projection framed at the default distance.
 *  Used at boot; the swap path builds from the live pose instead. */
export function buildDefaultCamera(mode: CameraProjection): SceneCamera {
    const aspect = window.innerWidth / window.innerHeight;
    if (mode === 'orthographic') {
        const halfH = DEFAULT_DISTANCE * HALF_FOV_TAN;
        const cam = new THREE.OrthographicCamera(
            -halfH * aspect, halfH * aspect, halfH, -halfH,
            CAMERA_DEFAULTS.near, CAMERA_DEFAULTS.far,
        );
        cam.zoom = 1;
        cam.position.set(CAMERA_DEFAULTS.position.x, CAMERA_DEFAULTS.position.y, CAMERA_DEFAULTS.position.z);
        cam.updateProjectionMatrix();
        return cam;
    }
    const cam = new THREE.PerspectiveCamera(
        CAMERA_DEFAULTS.fovDeg, aspect, CAMERA_DEFAULTS.near, CAMERA_DEFAULTS.far,
    );
    cam.position.set(CAMERA_DEFAULTS.position.x, CAMERA_DEFAULTS.position.y, CAMERA_DEFAULTS.position.z);
    return cam;
}

/** OrbitControls bound to `camera`, with both clamp pairs set so the relevant
 *  one applies per projection (distance for perspective, zoom for ortho). */
export function createControls(viewer: ViewerContext, camera: SceneCamera): OrbitControls {
    const renderer = viewer.three.renderer;
    if (!renderer) throw new Error('createControls: renderer not ready');
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = CAMERA_DEFAULTS.dampingFactor;
    controls.minDistance = CAMERA_DEFAULTS.minDistance;
    controls.maxDistance = CAMERA_DEFAULTS.maxDistance;
    controls.minZoom = CAMERA_DEFAULTS.minZoom;
    controls.maxZoom = CAMERA_DEFAULTS.maxZoom;
    controls.zoomToCursor = true; // wheel zoom moves toward the pointer, not the center
    return controls;
}

/** Swap the scene camera projection in place, matching the current framing so
 *  the view does not jump, then rebuild the camera-bound subsystems. */
export function setCameraProjection(viewer: ViewerContext, mode: CameraProjection): void {
    const old = viewer.three.camera;
    const controls = viewer.three.controls;
    if (!old || !controls || !viewer.three.renderer) return;
    const wantOrtho = mode === 'orthographic';
    if ((old instanceof THREE.OrthographicCamera) === wantOrtho) return; // no-op

    const target = controls.target.clone();
    const position = old.position.clone();
    const up = old.up.clone();
    const aspect = window.innerWidth / window.innerHeight;

    let next: SceneCamera;
    if (wantOrtho) {
        // Match the perspective framing: visible height at the target plane.
        const halfH = position.distanceTo(target) * HALF_FOV_TAN;
        const cam = new THREE.OrthographicCamera(
            -halfH * aspect, halfH * aspect, halfH, -halfH,
            CAMERA_DEFAULTS.near, CAMERA_DEFAULTS.far,
        );
        cam.zoom = 1;
        cam.position.copy(position);
        next = cam;
    } else {
        // Invert the framing: pick the distance whose perspective frustum shows
        // the same world height the ortho zoom was showing.
        const ortho = old as THREE.OrthographicCamera;
        const visibleHeight = (ortho.top - ortho.bottom) / ortho.zoom;
        const d = Math.min(
            CAMERA_DEFAULTS.maxDistance,
            Math.max(CAMERA_DEFAULTS.minDistance, visibleHeight / (2 * HALF_FOV_TAN)),
        );
        const dir = position.clone().sub(target);
        const len = dir.length();
        if (len > 1e-6) dir.multiplyScalar(1 / len); else dir.set(0, 0, 1);
        const cam = new THREE.PerspectiveCamera(
            CAMERA_DEFAULTS.fovDeg, aspect, CAMERA_DEFAULTS.near, CAMERA_DEFAULTS.far,
        );
        cam.position.copy(target).addScaledVector(dir, d);
        next = cam;
    }
    next.up.copy(up);
    applyCameraViewport(next);
    next.lookAt(target);
    next.updateMatrixWorld();
    viewer.three.camera = next;

    // OrbitControls binds its camera at construction — recreate it.
    controls.dispose();
    const newControls = createControls(viewer, next);
    newControls.target.copy(target);
    newControls.update();
    viewer.three.controls = newControls;

    // The gnomon's ViewHelper and the picking interactions capture the camera
    // at init — dispose + reinstall against the new one. (The loop reads live.)
    viewer.dispose.cameraGnomon?.();
    viewer.dispose.cameraGnomon = installCameraGnomon(viewer);
    viewer.dispose.selectionInteractions?.();
    viewer.dispose.selectionInteractions = reinitSelectionInteractions(viewer);

    // The swap recreated controls — re-apply the active nav mode so fly's
    // enabled=false / level-turntable up-vector survive the projection change.
    reapplyNavMode(viewer);

    viewer.loop.handle?.onResize();
    saveProjection(mode);
}
